import { useCallback, useRef, useState, type ReactNode } from 'react'
import { Bell, Smartphone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { useToast } from '../ui/use-toast'
import { usePushNudge } from '../../hooks/usePushNudge'
import { canShowPushPopup, dismissPushPopup, markPushPopupShown } from '../../utils/push-nudge-storage'
import { detectInstallPlatform } from '../../utils/install-prompt'
import { trackEvent } from '../../utils/analytics'
import { InstallStepsList } from '../pwa/InstallSteps'

/** The moments a popup may ask (docs/PUSH_OPT_IN_PLAN.md, "The moments"). */
export type PushDialogSource = 'follow_gym' | 'follow_crag' | 'follow_user' | 'send'

interface UsePushNudgeDialogReturn {
  /**
   * Opens the ask if this member should be asked and the shared budget allows
   * one now. Returns whether it opened. The caller is the one that knows
   * nothing else is on screen.
   */
  maybeOpen: (params?: { name?: string }) => boolean
  /** To be rendered by the caller. */
  dialog: ReactNode
}

/**
 * The ask to turn push on, right after the thing it would have told you
 * about: following a gym, a crag or a climber, a send that is a first top.
 *
 * Our own dialog first; the browser's prompt only behind its button, so the
 * browser's one-off question is spent on people who already said yes. On an
 * iPhone outside the app it shows the install steps instead. All the popups
 * share one budget: one every three days at most, longer after each "not now",
 * and never after the third (`push-nudge-storage`).
 */
export function usePushNudgeDialog(source: PushDialogSource): UsePushNudgeDialogReturn {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { nudge, userId, enable, busy } = usePushNudge()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState<string | undefined>()
  const answered = useRef(false)

  const maybeOpen = useCallback((params?: { name?: string }) => {
    if (!userId || (nudge !== 'ask' && nudge !== 'install')) return false
    if (!canShowPushPopup(userId)) return false

    markPushPopupShown(userId)
    answered.current = false
    setName(params?.name)
    setOpen(true)
    trackEvent('push_nudge_shown', { source, state: nudge })
    return true
  }, [userId, nudge, source])

  const close = (next: boolean) => {
    if (next) return
    if (!answered.current && userId) {
      dismissPushPopup(userId)
      trackEvent('push_nudge_dismissed', { source, state: nudge })
    }
    setOpen(false)
  }

  const accept = async () => {
    answered.current = true
    trackEvent('push_nudge_accepted', { source, state: nudge })
    try {
      await enable(source)
      trackEvent('push_enabled', { source })
      toast({ title: t('pushNudge.enabledToast.title'), description: t('pushNudge.enabledToast.description') })
    } catch {
      trackEvent('push_permission_denied', { source })
      toast({ title: t('pushNudge.deniedToast.title'), description: t('pushNudge.deniedToast.description') })
    } finally {
      setOpen(false)
    }
  }

  const installPlatform = nudge === 'install' ? detectInstallPlatform() : null

  const dialog = (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            {nudge === 'install' ? <Smartphone className="h-6 w-6" /> : <Bell className="h-6 w-6" />}
          </span>
          <DialogTitle className="text-center">{t(`pushNudge.dialog.${source}.title`)}</DialogTitle>
          <DialogDescription className="text-center">
            {t(`pushNudge.dialog.${source}.description`, { name })}
          </DialogDescription>
        </DialogHeader>

        {nudge === 'install' && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">{t('pushNudge.install.description')}</p>
            {installPlatform && <InstallStepsList platform={installPlatform} />}
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {nudge !== 'install' && (
            <Button onClick={accept} disabled={busy}>
              <Bell className="mr-2 h-4 w-4" />
              {t('pushNudge.dialog.enable')}
            </Button>
          )}
          <Button variant="ghost" onClick={() => close(false)}>
            {t('pushNudge.dialog.notNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return { maybeOpen, dialog }
}
